/*
 * BuyExtraCreditsButton — opens a small dialog letting Pro/Enterprise
 * customers purchase 1-10 extra AI generation packs in a single Stripe
 * Checkout session.
 *
 * The component is plan-aware via the `quota` snapshot returned by
 * GET /api/seo/quota:
 *   - Starter (extra_pack_offered=false)  -> button is NOT rendered.
 *   - Pro / Enterprise (extra_pack_offered=true) -> dialog enabled when
 *     extra_pack_purchasable=true; if false (price id placeholder), the
 *     dialog opens but the confirm button is disabled with a hint.
 *
 * Purchased credits are credited server-side after Stripe webhook
 * confirmation (workspaces.ai_generations_purchased) and NEVER expire.
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Sparkles, Loader2, Plus, Minus } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import api from '@/lib/api';
import { toast } from 'sonner';

const MIN_PACKS = 1;
const MAX_PACKS = 10;

export function BuyExtraCreditsButton({ quota, variant = 'default', size = 'sm', className }) {
  const { language } = useLanguage();
  const tf = (fr, en) => (language === 'fr' ? fr : en);
  const [open, setOpen] = useState(false);
  const [packs, setPacks] = useState(1);
  const [loading, setLoading] = useState(false);

  if (!quota || !quota.extra_pack_offered) return null;

  const packQuantity = Number(quota.extra_quota_quantity || 0);
  const packPrice = Number(quota.extra_quota_price || 0);
  const totalCredits = packQuantity * packs;
  const totalPrice = packPrice * packs;
  const canCheckout = quota.extra_pack_purchasable === true;

  const handleCheckout = async () => {
    if (!canCheckout || loading) return;
    setLoading(true);
    try {
      const { data } = await api.post('/billing/checkout/extra-quota', { quantity: packs });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      toast.error(tf('Erreur Stripe (pas d\'URL de paiement).', 'Stripe error (no checkout URL).'));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || tf('Erreur lors de la création du paiement.', 'Failed to create checkout session.'));
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const dec = () => setPacks((p) => Math.max(MIN_PACKS, p - 1));
  const inc = () => setPacks((p) => Math.min(MAX_PACKS, p + 1));

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={() => setOpen(true)}
        className={className}
        data-testid="buy-ai-credits-btn"
      >
        <Sparkles className="h-4 w-4 mr-1.5" />
        {tf('Acheter des crédits IA', 'Buy AI credits')}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="buy-ai-credits-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              {tf('Acheter des crédits IA supplémentaires', 'Buy extra AI credits')}
            </DialogTitle>
            <DialogDescription>
              {tf(
                `Chaque pack = ${packQuantity} générations IA. Crédits non-expirables : ils restent disponibles jusqu'à utilisation, même après le reset mensuel.`,
                `Each pack = ${packQuantity} AI generations. Non-expiring credits: they remain available until used, even after the monthly reset.`,
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Quantity selector */}
            <div className="flex items-center justify-center gap-3" data-testid="buy-ai-credits-qty">
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={dec}
                disabled={packs <= MIN_PACKS || loading}
                data-testid="buy-ai-credits-dec"
                aria-label={tf('Diminuer', 'Decrease')}
              >
                <Minus className="h-4 w-4" />
              </Button>
              <div className="min-w-[5rem] text-center">
                <div className="text-3xl font-bold tabular-nums" data-testid="buy-ai-credits-packs">
                  {packs}
                </div>
                <div className="text-xs text-muted-foreground">
                  {packs > 1 ? tf('packs', 'packs') : tf('pack', 'pack')}
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={inc}
                disabled={packs >= MAX_PACKS || loading}
                data-testid="buy-ai-credits-inc"
                aria-label={tf('Augmenter', 'Increase')}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            {/* Summary */}
            <div className="rounded-lg border bg-muted/30 p-3 space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{tf('Crédits ajoutés :', 'Credits added:')}</span>
                <span className="font-semibold tabular-nums" data-testid="buy-ai-credits-total-credits">
                  +{totalCredits}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{tf('Prix unitaire :', 'Unit price:')}</span>
                <span className="tabular-nums">${packPrice} CAD</span>
              </div>
              <div className="flex justify-between border-t pt-1 mt-1">
                <span className="font-semibold">{tf('Total :', 'Total:')}</span>
                <span className="font-bold tabular-nums" data-testid="buy-ai-credits-total-price">
                  ${totalPrice} CAD
                </span>
              </div>
            </div>

            {!canCheckout && (
              <div className="text-xs p-2 rounded-md border border-amber-300 bg-amber-50 text-amber-800" data-testid="buy-ai-credits-unavailable">
                {tf(
                  'Le pack d\'achat n\'est pas encore configuré sur ce serveur (Stripe Price ID manquant). Contactez le support.',
                  'Extra-pack purchase is not configured on this server (missing Stripe Price ID). Contact support.',
                )}
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={loading}
              data-testid="buy-ai-credits-cancel"
            >
              {tf('Annuler', 'Cancel')}
            </Button>
            <Button
              onClick={handleCheckout}
              disabled={!canCheckout || loading}
              data-testid="buy-ai-credits-confirm"
            >
              {loading ? (
                <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />{tf('Redirection...', 'Redirecting...')}</>
              ) : (
                tf('Payer avec Stripe', 'Pay with Stripe')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
